'use client';

import { FC, useCallback } from 'react';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { Grid } from '@purpur/library';

import { Carousel } from '@telialt/common-carousel/src';
import { useInitializeTranslations } from '@telialt/common-cms/src/useTranslations';
import { ConnectedProductCard } from '@telialt/core-search/src/Common/ConnectedProductCard/ConnectedProductCard';
import { useLoadProductsCustomQuery } from '@telialt/core-search/src/hooks/useLoadProductsCustomQuery';
import { useLoadQueryKeys } from '@telialt/core-search/src/hooks/useLoadQueryKeys';
import { ProductSearchItem } from '@telialt/core-search/src/model';
import { trackSelectItemGA, trackViewItemListGA } from '@telialt/gtm-tracking/src/events';
import { useTrackOnPageLoad } from '@telialt/gtm-tracking/src/lib/core/loadTracking';
import { useSegment } from '@telialt/utils/src/segment';
import { useUserData } from '@telialt/utils/src/SSO/useUserData';

import { BlockProductsType } from '../../models/Content';
import { getBlockId } from '../../utils/content';
import { BlockHeader } from '../common/BlockHeader';
import { BlockProductsSection, BlockProductsSlide } from './BlockProducts.styles';
import { getDefaultProductsQueryKey } from './getDefaultProductsQueryKey';
import { transformBlockProducts } from './transformers';

type Props = {
    contentItem: BlockProductsType;
    gridContainerClassName?: string;
};

export const BlockProducts: FC<Props> = ({ contentItem: initialContentItem, gridContainerClassName }) => {
    const { t } = useInitializeTranslations();

    const contentItem = useContentfulLiveUpdates(initialContentItem);

    const data = transformBlockProducts(contentItem);
    const segment = useSegment();
    const { isLoadingUserData, userData } = useUserData();
    const needsDefaultQueryKey = !data?.queryKey && !!data?.searchFilters;
    const { queryKeys, isLoading: isQueryKeysLoading } = useLoadQueryKeys(needsDefaultQueryKey);
    const queryKey =
        data?.queryKey ?? (needsDefaultQueryKey ? getDefaultProductsQueryKey(segment, queryKeys) : undefined);

    const { data: products, isLoading } = useLoadProductsCustomQuery({
        queryKey,
        filters: data?.searchFilters,
        sort: data?.sort,
        limit: data?.limit,
    });

    const equipmentProducts = products?.items;
    const equipmentListKey = `${queryKey || ''}:${(equipmentProducts || [])
        .map((product) => product.variantCode || product.code)
        .join('|')}`;
    const trackEquipmentList = useCallback(() => {
        trackViewItemListGA({
            source: 'equipmentList',
            data: {
                products: equipmentProducts || [],
                itemListName: data?.blockTitle || '',
                itemListId: queryKey || '',
                itemCategory: data?.blockTitle || '',
                segment,
                userData,
            },
        });
    }, [data?.blockTitle, equipmentProducts, queryKey, segment, userData]);

    useTrackOnPageLoad(
        Boolean(queryKey) && !isQueryKeysLoading && !isLoading && !isLoadingUserData,
        equipmentListKey,
        trackEquipmentList,
    );

    if (!t || !queryKey || isQueryKeysLoading || isLoading || !products?.items?.length) {
        return null;
    }

    const handleProductClick = (product: ProductSearchItem, index: number) => {
        trackSelectItemGA({
            source: 'equipmentList',
            data: {
                products: [product],
                itemListName: data.blockTitle || '',
                itemListId: queryKey || '',
                itemCategory: data.blockTitle || '',
                segment,
                userData,
                index,
            },
        });
    };

    const slides = products.items.map((product, index) => (
        <BlockProductsSlide key={product.id} data-section="Carousel Card" data-section-index={index + 1}>
            <ConnectedProductCard
                product={product}
                headingTag="h3"
                onClick={() => handleProductClick(product, index)}
            />
        </BlockProductsSlide>
    ));

    return (
        <BlockProductsSection
            data-section="Product Carousel"
            data-section-name={data.blockTitle}
            id={getBlockId(contentItem)}
        >
            <Grid noGap={true} containerClassName={gridContainerClassName}>
                <BlockHeader title={data.blockTitle} cta={data.blockCta} />
                <Carousel variant="dynamic-width" slides={slides} noControlsMargin={true} />
            </Grid>
        </BlockProductsSection>
    );
};
